import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import TextInput from './inputs/text-input'
import SelectInput from './inputs/select-input'
import PhoneInputWrapper from './inputs/phone-input'
import DateInput from './inputs/date-input'
import DatePicker from 'react-datepicker'
import TextArea from './inputs/text-area'
import MultiSelect from './inputs/multi-select'
import { postForm } from '../../../ajax'

export default function Signup({token, type, league, defaults, onBack, onComplete}) {
  const defaultValues = {
    first_name: '',
    last_name: '',
    gender: 'male',
    email: '',
    phone: '+1 ',
    birthday: moment().format('MM/DD/YYYY'),
    skill_level: '',
    prefer_position: '',
    shirt_size: ''
  }
  const { register, control, handleSubmit, setValue, formState: { errors } } = useForm(
    { defaultValues: defaults || defaultValues }
  )
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)
  const [countryCode, setCountryCode] = useState()
  const [changes, setChanges] = useState({})
  const requiredMsg = 'Is required'
  const genderOptions = [['male', 'Male'], ['female', 'Female'], ['prefer not to say', 'Prefer not to say']]
  const skillLevelOptions = [['', 'Select'], [0, 'Beginner'], [1, 'Intermediate'], [2, 'Advanced'], [3, 'Expert']]
  const sizeOptions = [['', 'Select'], ['XS', 'XS'], ['S', 'S'], ['M', 'M'], ['L', 'L'], ['XL', 'XL'], ['XXL', 'XXL']]
  const positionOptions = ['Forward', 'Winger', 'Center Midfielder', 'Right / Left Back', 'Center Back', 'Goalie'].map(val => ({value: val, label: val}))
  const anyChanges = Object.values(changes).some(x => x)

  const validateText = (value) => {
    if (!value.trim()) return requiredMsg
  }

  const changeValue = (name, value) => {
    setValue(name, value)
    setChanges({...changes, [name]: value != defaultValues[name] })
  }

  const onClickBack = (e) => {
    e.stopPropagation()
    if (!anyChanges) return onBack()
    if (window.confirm('Are you sure you want to go back? All progress will be lost')) onBack()
  }

  const prepareFormData = (data) => {
    let formData = new FormData()
    formData.append('token', token)
    formData.append('player[first_name]', data.first_name.trim())
    formData.append('player[last_name]', data.last_name.trim())
    formData.append('player[gender]', data.gender)
    formData.append('player[email]', data.email)
    formData.append('player[phone]', data.phone)
    if (countryCode) formData.append('player[country_code]', countryCode)
    formData.append('player[birthday]', moment(new Date(data.birthday)).format('DD/MM/YYYY'))
    formData.append('player[skill_level]', data.skill_level)
    formData.append('player[prefer_position]', data.prefer_position ? data.prefer_position.map(v => v.label).join(', ') : '')
    formData.append('player[shirt_size]', data.shirt_size)
    formData.append('player[self_desc]', data.self_desc || '')
    formData.append('player[team_desc]', data.team_desc || '')
    return formData
  }

  const onSubmit = async (data) => {
    setLoading(true)
    const formData = prepareFormData(data)
    const response = await postForm(`/plugin/leagues/${league.id}/registrations/signup`, formData)
    const json = await response.json()
    setLoading(false)
    if (json.error) setError(json.error)
    else onComplete({...data, id: json.id, new: true})
  }

  return <form className={'signup-form' + (loading ? ' isLoading' : '')} onSubmit={handleSubmit(onSubmit)}>
    <div className='signup-title'>CREATE ACCOUNT</div>
    { error && <div className='registration-error'>{error}</div> }
    { loading && <div className='animated-spinner'></div> }
    <div className='row'>
      <div className='col-xs-12 col-md-4'>
        <TextInput
          {...register('first_name', { required: requiredMsg, validate: validateText })}
          autoFocus
          label='First Name'
          error={errors.first_name}
          onChange={e => changeValue('first_name', e.target.value)}
        />
      </div>
      <div className='col-xs-12 col-md-4'>
        <TextInput
          {...register('last_name', { required: requiredMsg, validate: validateText })}
          label='Last Name'
          error={errors.last_name}
          onChange={e => changeValue('last_name', e.target.value)}
        />
      </div>
      <div className='col-xs-12 col-md-4'>
        <SelectInput
          {...register('gender')}
          label='Gender'
          options={genderOptions}
          onChange={e => changeValue('gender', e.target.value)}
        />
      </div>
    </div>

    <div className='row'>
      <div className='col-xs-12 col-md-4'>
        <TextInput
          {...register('email', {
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'invalid email address'
            } 
          })}
          label='Email'
          error={errors.email}
          onChange={e => changeValue('email', e.target.value)}
        />
      </div>
      <div className='col-xs-12 col-md-4'>
        <PhoneInputWrapper
          {...register('phone', { required: requiredMsg })}
          label='Phone Number'
          error={errors.phone}
          defaultValue={defaults && defaults.phone}
          onChange={(p, c) => {
            changeValue('phone', p)
            setCountryCode(c)
          }}
        />
      </div>
      <div className='col-xs-12 col-md-4'>
        <Controller
          control={control}
          name='birthday'
          render={({ field }) => (
            <DatePicker
              customInput={<DateInput label='Date of Birth' />}
              onChange={(date) => {
                const formattedDate = (date || moment()).format('MM/DD/YYYY')
                field.onChange(formattedDate)
                changeValue('birthday', formattedDate)
              }}
              selected={moment(new Date(field.value))}
            />
          )}
        />
      </div>
    </div>

    <div className='row'>
      <div className='col-xs-12 col-md-4'>
        <SelectInput
          {...register('skill_level', { required: requiredMsg })}
          label='Skill Level'
          options={skillLevelOptions}
          error={errors.skill_level}
          onChange={e => changeValue('skill_level', e.target.value)}
        />
      </div>
      <div className='col-xs-12 col-md-4'>
        <MultiSelect
          name='prefer_position'
          label='Preferred Position'
          required
          error={errors.prefer_position}
          options={positionOptions}
          control={control}
        />
      </div>
      <div className='col-xs-12 col-md-4'>
        <SelectInput
          {...register('shirt_size', { required: requiredMsg })}
          label='Shirt Size'
          options={sizeOptions}
          error={errors.shirt_size}
          onChange={e => changeValue('shirt_size', e.target.value)}
        />
      </div>
    </div>

    { type == 'individual' && 
      <div className='row describe-section'>
        <div className='col-xs-12 col-md-6'>
          <TextArea
            {...register('self_desc')}
            label='Describe Yourself'
            desc='Describe your experience and attitude toward this league. Captains browsing for players will be able to read this descripion as they search for players.'
            rows='4'
            onChange={(e) => changeValue('self_desc', e.target.value)}
          />
        </div>
        <div className='col-xs-12 col-md-6'>
          <TextArea
            {...register('team_desc')}
            label='Describe Your Ideal Team'
            desc="Describe what you're looking for in a team."
            rows='4'
            onChange={(e) => changeValue('team_desc', e.target.value)}
          />
        </div>
      </div>
    }

    <div className='row'>
      <div className='col-xs-12 text-center signup-buttons'>
        <div className='button back-button' onClick={onClickBack}>Back</div>
        <button className='button next-button'>Next</button>
      </div>
    </div>
  </form>
}
